import * as React from "react";
import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { useTranslate } from "react-admin";
import {
  ACCENT,
  BannerFormValue,
  BannerPreview,
  LOCALE,
  SectionTitleLike,
  getImageSrc,
  t,
} from "../creatorsShared/previewKit";
import { ExtraButton, PriceCategory, StoredImage, Translatable } from "../../types";

const SOFT_BG = "#FAF2EC";

interface FormSectionFormValue {
  cover?: { formImage?: StoredImage; formVideo?: string };
  formTitle?: Translatable<string>;
  formSubtitle?: Translatable<string>;
  formDescription?: Translatable<string>;
  formArticle?: Translatable<string>;
  extraButtons?: Translatable<ExtraButton[]>;
  prices?: Translatable<PriceCategory[]>;
}

interface ServicePreviewFormValues {
  service?: {
    cover?: StoredImage;
    serviceName?: Translatable<string>;
    coverTitle?: Translatable<string>;
    coverSubTitle?: Translatable<string>;
    mainPriceTitle?: Translatable<string>;
    mainPriceSubtitle?: Translatable<string>;
  };
  banner?: BannerFormValue;
  forms?: FormSectionFormValue[];
}

const FormSectionPreview = ({
  form,
  style,
}: {
  form: FormSectionFormValue;
  style: "ltr" | "rtl";
}) => {
  const imgSrc = getImageSrc(form?.cover?.formImage);
  const videoId = form?.cover?.formVideo;
  const extraButtons = form?.extraButtons?.[LOCALE] || [];
  const prices = form?.prices?.[LOCALE] || [];

  const media = (
    <Box sx={{ flex: 1 }}>
      {videoId ? (
        <Chip label={`YouTube: ${videoId}`} variant="outlined" />
      ) : imgSrc ? (
        <Box
          component="img"
          src={imgSrc}
          alt=""
          sx={{ width: "100%", borderRadius: 1, objectFit: "cover" }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: 160,
            bgcolor: "#eee",
            borderRadius: 1,
          }}
        />
      )}
      {extraButtons.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={1} mt={2}>
          {extraButtons.map((btn, i) => (
            <Chip key={i} variant="outlined" label={btn?.title || "..."} />
          ))}
        </Stack>
      )}
    </Box>
  );

  const content = (
    <Box sx={{ flex: 1 }}>
      <SectionTitleLike
        title={t(form?.formTitle)}
        article={t(form?.formSubtitle)}
      />
      <Typography sx={{ mt: 2, color: "text.secondary" }}>
        {t(form?.formArticle)}
      </Typography>
      <Box sx={{ bgcolor: SOFT_BG, p: 3, mt: 3, borderRadius: 1 }}>
        {form?.formDescription && (
          <Box
            sx={{ mb: 2 }}
            dangerouslySetInnerHTML={{ __html: t(form.formDescription) }}
          />
        )}
        <Stack direction="row" spacing={2}>
          <Chip label="Zamów online" sx={{ bgcolor: "#fff" }} />
          {prices.length > 0 && (
            <Chip label="Zobacz ceny" variant="outlined" />
          )}
        </Stack>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 4,
        py: 4,
      }}
    >
      {style === "rtl" ? (
        <>
          {content}
          {media}
        </>
      ) : (
        <>
          {media}
          {content}
        </>
      )}
    </Box>
  );
};

// Mirrors the carousel card in pl-front's ServicesSwiper.tsx (350x250, object-cover).
const ServiceCardPreview = ({
  cover,
  title,
  caption,
}: {
  cover?: StoredImage;
  title: string;
  caption: string;
}) => {
  const imgSrc = getImageSrc(cover);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        {caption}
      </Typography>
      <Box
        sx={{
          position: "relative",
          width: { xs: 290, md: 350 },
          maxWidth: "100%",
          height: 250,
          bgcolor: "#eee",
        }}
      >
        {imgSrc && (
          <Box
            component="img"
            src={imgSrc}
            alt=""
            sx={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            bottom: 0,
            background: "linear-gradient(to right, #7EA1CC, #B1C5DF, #7EA1CC)",
            color: "#fff",
            py: "5px",
            px: "22px",
            fontSize: { xs: 14, md: 16 },
            lineHeight: { xs: "22px", md: "27px" },
          }}
        >
          {title || "…"}
        </Box>
      </Box>
    </Box>
  );
};

const PreviewRender = ({ formData }: { formData: ServicePreviewFormValues }) => {
  const translate = useTranslate();
  const service = formData?.service || {};
  const banner = formData?.banner || {};
  const forms = formData?.forms || [];

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        <Chip
          size="small"
          label={`${translate(
            "services.serviceName"
          )}: ${t(service.serviceName) || "—"}`}
        />
        <Chip
          size="small"
          label={`SEO title: ${t(service.coverTitle) || "—"}`}
        />
        <Chip
          size="small"
          label={`SEO description: ${t(service.coverSubTitle) || "—"}`}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
        {translate("preview.metaHint")}
      </Typography>

      <ServiceCardPreview
        cover={service.cover}
        title={t(service.coverTitle)}
        caption={translate("preview.serviceCard")}
      />

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <BannerPreview banner={banner} />

        <Box sx={{ px: { xs: 3, md: 6 } }}>
          {forms.map((form, index) => (
            <FormSectionPreview
              key={index}
              form={form}
              style={index % 2 ? "ltr" : "rtl"}
            />
          ))}

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ sm: "center" }}
            spacing={2}
            sx={{ py: 4 }}
          >
            <SectionTitleLike
              title={t(service.mainPriceTitle)}
              article={t(service.mainPriceSubtitle)}
            />
            <AttachMoneyIcon sx={{ color: ACCENT, fontSize: 40 }} />
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default PreviewRender;
